import {
  ClipboardListIcon,
  EyeOffIcon,
  LockClosedIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import Link from 'next/link';
import type { ComponentType, SVGProps } from 'react';

import Halftone from '@/components/agents/Halftone';
import { ArrowGlyph, SectionHeading } from '@/components/agents/primitives';

import { ShieldGlyph, useB2bTranslation } from './kit';

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

const TRUST_CENTER_URL = 'https://easypeasyai.trust.site/';

export default function TeamsSecurity() {
  const { t } = useB2bTranslation();

  const standards: { name: string; detail: string }[] = [
    {
      name: 'SOC 2',
      detail: t('Controls for security, availability and confidentiality.'),
    },
    {
      name: 'ISO 27001',
      detail: t(
        'An information security management system to the international standard.',
      ),
    },
    {
      name: 'GDPR',
      detail: t('Personal data handled under EU data protection rules.'),
    },
    {
      name: 'HIPAA',
      detail: t(
        'A Business Associate Agreement for healthcare teams on paid plans.',
      ),
    },
  ];

  const promises: {
    icon: Icon;
    title: string;
    detail: string;
    href?: string;
    external?: boolean;
  }[] = [
    {
      icon: EyeOffIcon,
      title: t('No training on your data'),
      detail: t('We don’t use your inputs to train our own models.'),
    },
    {
      icon: LockClosedIcon,
      title: t('Encrypted in transit and at rest'),
      detail: t('On its way to us, and wherever it is stored.'),
    },
    {
      icon: TrashIcon,
      title: t('Deleted means deleted'),
      detail: t(
        'Delete a chat or empty the trash and the content is gone for good.',
      ),
    },
    {
      icon: ClipboardListIcon,
      title: t('Every sub-processor listed'),
      detail: t('See which providers process your data, and why.'),
      href: '/sub-processors',
    },
  ];

  return (
    <section
      id='security'
      className='relative isolate overflow-hidden bg-[#0c0b1c] py-20 sm:py-28'
    >
      <Halftone
        at='100% 0%'
        className='opacity-30 [--ht:rgb(var(--tw-color-primary-800))]'
        reach={70}
      />
      <div className='relative mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
          <SectionHeading
            invert
            title={t('Your data stays yours.')}
            tail={
              <span className='block'>
                {t('Built to the standards IT asks for.')}
              </span>
            }
            lead={t(
              'Our policies and security documentation are in one place, ready for your IT and legal teams.',
            )}
          />
          <a
            href={TRUST_CENTER_URL}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex flex-none items-center gap-2 self-start rounded-full px-5 py-3 text-[15px] font-semibold text-white ring-1 ring-inset ring-white/25 transition duration-200 hover:bg-white/10 hover:ring-white/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white lg:self-auto'
          >
            {t('Visit the Trust Center')}
            <ArrowGlyph className='-rotate-45 rtl:rotate-45' />
          </a>
        </div>

        <div className='mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
          {standards.map(({ name, detail }) => (
            <div
              key={name}
              className='rounded-[22px] bg-white/[0.04] p-6 ring-1 ring-inset ring-white/10'
            >
              <span className='flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-b from-white/15 to-white/[0.04] ring-1 ring-inset ring-white/15'>
                <ShieldGlyph className='h-6 w-6 text-emerald-300' />
              </span>
              <p className='mt-5 text-xl font-semibold tracking-[-0.01em] text-white'>
                {name}
              </p>
              <p className='mt-2 text-[15px] leading-7 text-[#b7b7d2]'>
                {detail}
              </p>
            </div>
          ))}
        </div>

        <ul className='mt-12 grid gap-x-8 gap-y-8 border-t border-white/10 pt-12 sm:grid-cols-2 lg:grid-cols-4'>
          {promises.map(({ icon: PromiseIcon, title, detail, href }) => (
            <li key={title} className='flex gap-4'>
              <PromiseIcon className='mt-0.5 h-5 w-5 flex-none text-primary-300' />
              <div>
                <p className='text-[15px] font-semibold text-white'>
                  {href ? (
                    <Link
                      href={href}
                      className='underline-offset-4 hover:underline'
                    >
                      {title}
                    </Link>
                  ) : (
                    title
                  )}
                </p>
                <p className='mt-1 text-[15px] leading-7 text-[#a4a4bd]'>
                  {detail}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
